import { Spin } from 'antd'
import { Ruler, useRolodexRulers } from 'features/fde'
import {
  LABEL_WIDTH,
  SectionContainer,
  Sub,
  TooltipDivider,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { useFfsStrategy } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useGeckoLocale } from 'features/locale'
import isNil from 'lodash/isNil'
import minBy from 'lodash/minBy'
import React, { FC, useMemo } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { LABEL_TC, LABEL_TMM } from './FfsDataValuesSection'
import { LABEL_FCA_ML } from './FfsLevelOneUserInputSection'

export interface FfsFlawSizeCriteraSectionProps {
  loading: boolean
  flawSizeCheckResult: Omit<FlawSizeCriteriaCheckResult, 'isLoaing'>
}

export const LABEL_R_T = (
  <>
    R<Sub>t</Sub>
  </>
)

export const LABEL_TMM_MINUS_FCA = (
  <>
    t<Sub>mm</Sub> - FCA<Sub>ml</Sub>
  </>
)

export const LABEL_LMSD = (
  <>
    L<Sub>msd</Sub>
  </>
)

export const LABEL_LMSD_FLAW_SIZE_CRITERIA = (
  <>
    {LABEL_LMSD} ≥ 1.8 * sqrt(D t<Sub>c</Sub>)
  </>
)

const TOOLTIP_LMSD = (
  <>
    Distance to the nearest major structural discontinuity
    <TooltipDivider />
    {LABEL_LMSD} ≥ 1.8 × sqrt(D × t<Sub>c</Sub>)
  </>
)

const TOOLTIP_R_T = (
  <>
    Remaining Thickness Ratio
    <TooltipDivider />({LABEL_TMM} - {LABEL_FCA_ML}) / {LABEL_TC}
  </>
)

interface FlawSizeCriteriaProps {
  tmm: GeckoMeasurement | null
  fca: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  indicationId: string | undefined
  tc: GeckoMeasurement | null
  d: GeckoMeasurement | null
}

const WORKING_UNIT = GeckoMeasurementUnit.Millimeters
const withWorkingUnit: (val: number) => GeckoMeasurement = (val) =>
  GeckoMeasurement.CreateFromValue(val, WORKING_UNIT)

type FlawSizeCriteria = 'remainingThicknessRatio' | 'min_thickness' | 'lmsd'

export type FlawSizeCriteriaCheckResult = {
  isLoading: boolean
  criteriaValues: Record<FlawSizeCriteria, GeckoMeasurement | null>
  failingCriteria: ReadonlySet<FlawSizeCriteria>
  lmsdThreshold: GeckoMeasurement | null
  lossThreshold: GeckoMeasurement
  remainingThicknessThreshold: GeckoMeasurement | null
}

function getRulerLength(ruler: Ruler): GeckoMeasurement {
  const xRange = ruler.selection.x.convertTo(WORKING_UNIT)
  const yRange = ruler.selection.y.convertTo(WORKING_UNIT)
  const xLen = Math.abs(xRange.max.num - xRange.min.num)
  const yLen = Math.abs(yRange.max.num - yRange.min.num)
  return GeckoMeasurement.CreateFromValue(
    Math.sqrt(Math.pow(xLen, 2) + Math.pow(yLen, 2)),
    WORKING_UNIT
  )
}

function getLmsdThreshold(
  d: GeckoMeasurement | null,
  tc: GeckoMeasurement | null
): GeckoMeasurement | null {
  return isNil(d) || isNil(tc)
    ? null
    : GeckoMeasurement.CreateFromValue(
        1.8 *
          Math.sqrt(
            d.convertTo(WORKING_UNIT).num * tc.convertTo(WORKING_UNIT).num
          ),
        WORKING_UNIT
      )
}

export function useLevelOneFlawSizeCriteriaCheck({
  tmm,
  fcaMl,
  fca,
  tc,
  d,
  indicationId,
}: FlawSizeCriteriaProps): FlawSizeCriteriaCheckResult {
  const {
    strategy: {
      analysisResult: { criteriaThicknessThreshold },
    },
  } = useFfsStrategy()

  const { rulers, isLoading: rulersLoading } = useRolodexRulers(
    useMemo(
      () => (indicationId ? { indication: indicationId } : undefined),
      [indicationId]
    )
  )

  // Default remaining thickness threshold ratio of 0.2
  const remainingThicknessThreshold = useMemo(
    () => GeckoMeasurement.CreateFromValue(0.2, GeckoMeasurementUnit.Ratio),
    []
  )

  const rt = useMemo(
    () =>
      isNil(tmm) || isNil(fcaMl) || isNil(tc) || isNil(fca)
        ? null
        : GeckoMeasurement.CreateFromValue(
            (tmm.convertTo(WORKING_UNIT_THICKNESS).num -
              fcaMl.convertTo(WORKING_UNIT_THICKNESS).num) /
              tc.convertTo(WORKING_UNIT_THICKNESS).num,
            GeckoMeasurementUnit.Ratio
          ),
    [tc, fca, fcaMl, tmm]
  )

  return useMemo(() => {
    const lossThreshold = criteriaThicknessThreshold
    const lmsdThreshold = getLmsdThreshold(d, tc)
    const criteriaValues = {
      remainingThicknessRatio: rt,
      min_thickness:
        isNil(tmm) || isNil(fcaMl)
          ? null
          : withWorkingUnit(
              tmm.convertTo(WORKING_UNIT).num -
                fcaMl?.convertTo(WORKING_UNIT).num
            ),
      lmsd: minBy(rulers?.map(getRulerLength), (meas) => meas.num) ?? null,
    }

    const failingCriteria = new Set<FlawSizeCriteria>()

    if (
      isNil(criteriaValues.remainingThicknessRatio) ||
      criteriaValues.remainingThicknessRatio.num <
        remainingThicknessThreshold.num
    ) {
      failingCriteria.add('remainingThicknessRatio')
    }
    if (
      isNil(criteriaValues.min_thickness) ||
      isNil(lossThreshold) ||
      criteriaValues.min_thickness.convertTo(GeckoMeasurementUnit.Millimeters)
        .num < lossThreshold?.num
    ) {
      failingCriteria.add('min_thickness')
    }
    if (
      isNil(criteriaValues.lmsd) ||
      isNil(lmsdThreshold) ||
      criteriaValues.lmsd.num < lmsdThreshold.num
    ) {
      failingCriteria.add('lmsd')
    }

    return {
      isLoading: rulersLoading,
      criteriaValues,
      failingCriteria,
      lmsdThreshold,
      lossThreshold,
      remainingThicknessThreshold,
    }
  }, [
    d,
    fcaMl,
    rt,
    rulers,
    rulersLoading,
    tc,
    tmm,
    criteriaThicknessThreshold,
    remainingThicknessThreshold,
  ])
}

const TooltipContentTmmFcaLimit: FC<{
  lossThreshold: GeckoMeasurement
}> = ({ lossThreshold }) => {
  const { formatMeasurement } = useGeckoLocale()
  return (
    <>
      {LABEL_TMM} - {LABEL_FCA_ML} {'≥'}{' '}
      <span>{formatMeasurement({ measurement: lossThreshold })}</span>
    </>
  )
}

const RawFfsFlawSizeCriteriaSection: FC<FfsFlawSizeCriteraSectionProps> = ({
  loading,
  flawSizeCheckResult,
}) => {
  const {
    criteriaValues: flawSizeCriteriaCheckValues,
    failingCriteria: flawSizeFailingCritera,
    lossThreshold,
    lmsdThreshold,
    remainingThicknessThreshold,
  } = flawSizeCheckResult
  const {
    strategy: {
      analysisResult: { criteriaThicknessThreshold },
    },
  } = useFfsStrategy()
  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterField
          label={LABEL_R_T}
          labelTooltip={TOOLTIP_R_T}
          labelWidth={LABEL_WIDTH}
          value={flawSizeCriteriaCheckValues.remainingThicknessRatio}
          measurementType='ratio'
          isValid={!flawSizeFailingCritera.has('remainingThicknessRatio')}
          trailingText={
            remainingThicknessThreshold
              ? `≥ ${remainingThicknessThreshold.num.toFixed(2)}`
              : undefined
          }
        />
        <FfsParameterField
          label={LABEL_TMM_MINUS_FCA}
          labelTooltip={
            <TooltipContentTmmFcaLimit
              lossThreshold={criteriaThicknessThreshold}
            />
          }
          labelWidth={LABEL_WIDTH}
          value={flawSizeCriteriaCheckValues.min_thickness}
          isValid={!flawSizeFailingCritera.has('min_thickness')}
          trailingText={(value) => {
            const thresholdInValueUnit = lossThreshold?.convertTo(
              value?.unit ?? lossThreshold.unit
            )
            return `≥ ${thresholdInValueUnit.num.toFixed(2)}`
          }}
        />
        <FfsParameterField
          label={LABEL_LMSD}
          labelTooltip={TOOLTIP_LMSD}
          labelWidth={LABEL_WIDTH}
          value={flawSizeCriteriaCheckValues.lmsd}
          isValid={!flawSizeFailingCritera.has('lmsd')}
          trailingText={(value) => {
            const unit = value?.unit
            const thresholdInValueUnit = lmsdThreshold?.convertTo(
              unit ?? WORKING_UNIT
            )
            return `≥ ${thresholdInValueUnit?.num.toFixed(2)}`
          }}
        />
      </SectionContainer>
    </Spin>
  )
}

export const FfsFlawSizeCriteriaSection = React.memo(
  RawFfsFlawSizeCriteriaSection
)
